import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import NudgePopup from "./nudgePopup";
import { globalNudgeObjectPopupData } from "@core/dictionary/intel-nudge";
import { globalAPIDictionary as globalApiEndPoints } from "@core/global/global-country-dictionary";
import { hasOwn } from "@core/utility/utility";

const NUDGE_CACHE_KEY = "nudgeCache";
const TEXT_FIELDS = ["header", "headerOnTop", "description", "subText1", "subheaders", "highlightedText"] as const;

type NudgeStatus = {
  shouldPop?: boolean;
  header?: string;
  headerOnTop?: boolean;
  description?: string;
  subText1?: string;
  subheaders?: unknown;
  highlightedText?: string;
  timer?: number;
  [key: string]: unknown;
};

type NudgeStateObj = Record<string, NudgeStatus>;

type FeatureNudgeApiItem = {
  feature?: string;
  should_pop?: boolean;
  text?: {
    header?: string;
    headerOnTop?: boolean;
    sub?: string;
    subText1?: string;
    subheaders?: unknown;
    highlightedText?: string;
  };
};

type FeatureNudgeProps = {
  nudgeKey: string;
  enabled?: boolean;
  /** Dev-only: bypass API/cache and immediately show the nudge using local dictionary data. */
  forceShow?: boolean;
};

const readNudgeCache = (): NudgeStateObj | null => {
  try {
    const cache = JSON.parse(localStorage.getItem(NUDGE_CACHE_KEY) as string);
    return cache && typeof cache === "object" ? (cache as NudgeStateObj) : null;
  } catch {
    return null;
  }
};

const writeNudgeCache = (obj: NudgeStateObj): void => {
  try {
    localStorage.setItem(NUDGE_CACHE_KEY, JSON.stringify(obj));
  } catch {
    /* ignore quota/serialization errors */
  }
};

// Build the render object from the dictionary (htmlId, image, button text) and overlay the
// should_pop / text status coming from cache or the backend. This guarantees display fields are
// always present even if an older nudgeCache predates this nudge.
const buildStateObj = (statusByFeature: NudgeStateObj | null): NudgeStateObj => {
  const base: NudgeStateObj = JSON.parse(JSON.stringify(globalNudgeObjectPopupData));
  if (statusByFeature) {
    Object.keys(base).forEach((feature) => {
      const status = statusByFeature[feature];
      if (!status) return;
      if (status.shouldPop !== undefined) base[feature].shouldPop = status.shouldPop;
      TEXT_FIELDS.forEach((field) => {
        if (status[field] !== undefined) (base[feature] as Record<string, unknown>)[field] = status[field];
      });
    });
  }
  return base;
};

/**
 * Self-contained feature-nudge for surfaces that don't already orchestrate nudges
 * (the Product Search / Company Search routes and the suppliers tab header).
 *
 * Backend contract (same as shipments.jsx):
 *   - GET featureNudge?plan_id=<plan>  -> [{ feature, should_pop, text }]
 *   - GET featureNudgeUpdate?is_popped=true&feature_name=<key>
 *   - localStorage "nudgeCache" for show-once persistence
 *
 * Renders <NudgePopup> anchored to the element whose id matches the dictionary htmlId.
 * `enabled` should track whether that anchor element is actually mounted.
 */
export default function FeatureNudge({ nudgeKey, enabled = true, forceShow = false }: FeatureNudgeProps) {
  const userDetails = useSelector((state: any) => state.userDetails);
  const currentUserPlan: string | undefined = userDetails?.creditDetails?.data?.[0]?.user_active_plan;

  const [nudgePopupStateObj, setNudgePopupStateObj] = useState<NudgeStateObj | null>(null);
  const [show, setShow] = useState(false);

  const shouldPop = useMemo(() => !!nudgePopupStateObj?.[nudgeKey]?.shouldPop, [nudgePopupStateObj, nudgeKey]);

  // Resolve should_pop for this nudge from cache, falling back to the backend.
  useEffect(() => {
    if (!enabled) return;
    if (forceShow) {
      setNudgePopupStateObj((prev) => {
        const base = prev ?? buildStateObj(null);
        return { ...base, [nudgeKey]: { ...base[nudgeKey], shouldPop: true } };
      });
      return;
    }
    let cancelled = false;

    const cached = readNudgeCache();
    // Trust the cache only if it already knows about THIS nudge; otherwise refresh from the API
    // so newly added nudges aren't permanently hidden by a stale cache.
    if (cached && hasOwn(cached, nudgeKey)) {
      setNudgePopupStateObj(buildStateObj(cached));
      return;
    }

    axios
      .get(globalApiEndPoints.featureNudge + `?plan_id=${currentUserPlan}`)
      .then((res) => {
        const data = res.data as FeatureNudgeApiItem[];
        if (cancelled || !Array.isArray(data)) return;
        const merged: NudgeStateObj = { ...(cached || {}) };
        data.forEach((item) => {
          if (!item.feature) return;
          merged[item.feature] = {
            ...(merged[item.feature] || {}),
            shouldPop: item.should_pop !== undefined ? item.should_pop : false,
            header: item.text?.header || "",
            headerOnTop: item.text?.headerOnTop || false,
            description: item.text?.sub || "",
            subText1: item.text?.subText1 || "",
            subheaders: item.text?.subheaders || "",
            highlightedText: item.text?.highlightedText || "",
          };
        });
        writeNudgeCache(merged);
        if (!cancelled) setNudgePopupStateObj(buildStateObj(merged));
      })
      .catch(() => {
        /* silent: a missing nudge status should never break the page */
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, forceShow, currentUserPlan, nudgeKey]);

  // Defer opening so the anchor element is painted (mirrors shipments.jsx timer behaviour).
  useEffect(() => {
    if (!enabled || !shouldPop) return;
    const timer = setTimeout(() => setShow(true), forceShow ? 0 : (nudgePopupStateObj?.[nudgeKey]?.timer ?? 1000));
    return () => clearTimeout(timer);
  }, [enabled, forceShow, shouldPop, nudgeKey, nudgePopupStateObj]);

  const handleNudgePopup = (): void => {
    axios.get(globalApiEndPoints.featureNudgeUpdate + `?is_popped=true&feature_name=${nudgeKey}`);
    const tempObj = readNudgeCache() || {};
    tempObj[nudgeKey] = { ...(tempObj[nudgeKey] || {}), shouldPop: false };
    writeNudgeCache(tempObj);
    setNudgePopupStateObj((prev) => {
      if (!prev?.[nudgeKey]) return prev;
      return { ...prev, [nudgeKey]: { ...prev[nudgeKey], shouldPop: false } };
    });
    setShow(false);
  };

  if (!enabled || !nudgePopupStateObj || (!forceShow && (!show || !shouldPop))) return null;

  return <NudgePopup nudgePopupStateObj={nudgePopupStateObj} currentNudgePopupKey={nudgeKey} handleNudgePopup={handleNudgePopup} />;
}
